import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { LtdService } from '@/api/ltd-service';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { invalidatePlanQueries } from '@/hooks/query-options';

export const Route = createFileRoute('/_auth/claim')({
  validateSearch: (s: Record<string, unknown>): { session_id?: string } => ({
    session_id: typeof s.session_id === 'string' ? s.session_id : undefined,
  }),
  component: ClaimPage,
});

function ClaimPage() {
  const { session_id } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const claim = useMutation({
    mutationFn: () => LtdService.claim(session_id),
    onSuccess: async () => {
      // Refresh plan, billing and credits before the trial banner renders.
      await invalidatePlanQueries(queryClient);
      toast.success('Your lifetime deal is active. Welcome aboard!');
      // The side layout retains search params, so session_id must be cleared explicitly.
      navigate({ to: '/', search: (prev) => ({ ...prev, session_id: undefined }) });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="bg-background flex min-h-dvh items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col gap-4 p-8 text-center">
          <h1 className="text-xl font-semibold">Claim Your Lifetime Deal</h1>
          <p className="text-muted-foreground text-sm">
            Apply your purchase to your current workspace. Your projects and data
            stay exactly where they are.
          </p>
          {error && <p className="text-destructive text-sm">{error}</p>}
          <Button
            onClick={() => {
              setError(null);
              claim.mutate();
            }}
            loading={claim.isPending}
            className="w-full"
          >
            Apply to My Workspace
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
